/**
 * portalFetch / portalSend — the REQUESTER-domain HTTP helpers (Step 71).
 * The mirror of apiFetch/apiSend with one crucial difference: a 401 sends
 * the person to the PORTAL login, never the agent one (three doors, never
 * crossed — each domain redirects to its own).
 */

export class PortalApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

function redirectToPortalLogin() {
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/portal/login?returnTo=${returnTo}`);
}

export async function portalFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (res.status === 401) {
    redirectToPortalLogin();
    throw new PortalApiError(401, 'Session expired', null);
  }
  const body = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) throw new PortalApiError(res.status, (body as any)?.error ?? `HTTP ${res.status}`, body);
  return body as T;
}

export interface PortalSendResult<T> {
  ok: boolean;
  status: number;
  body: T | null;
  error: string | null;
  errorBody?: unknown;
}

export async function portalSend<T>(
  path: string,
  method: string,
  bodyIn?: unknown,
): Promise<PortalSendResult<T>> {
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
  };
  if (bodyIn !== undefined) init.body = JSON.stringify(bodyIn);
  const res = await fetch(path, init);
  if (res.status === 401) {
    redirectToPortalLogin();
    return { ok: false, status: 401, body: null, error: 'Session expired' };
  }
  const body = res.status === 204 ? null : await res.json().catch(() => null);
  return {
    ok: res.ok,
    status: res.status,
    body: res.ok ? (body as T) : null,
    error: res.ok ? null : ((body as any)?.error ?? `HTTP ${res.status}`),
    ...(res.ok ? {} : { errorBody: body }),
  };
}
